import React, { useContext, Fragment } from 'react'
import Loadable from 'react-loadable'
import { Route, BrowserRouter as Router, Switch, Redirect } from 'react-router-dom'
import AuthProvider, { AuthContext } from './components/AuthProvider'
import BouncingLoader from './components/BouncingLoader'
import Sidebar from './components/Sidebar'
import { LOGOUT_URL_PATH, ROLE_OIS_ADMIN, ROLE_OIS_STAFF, ROLE_MEDIA_AGENCY, ROLE_MEDIA_OWNER, ROLE_CREATIVE_AGENCY } from './constants';
import { getRouteDefaultDates } from './utils/shared';
import GlobalStore, { GlobalStoreContext } from './state/global.store';

const defaultDates = getRouteDefaultDates();

const googleAnalyticsTracking = (history) => {
  history.listen((location) => {
    window.gtag('config', 'UA-178280693-1', { page_path: location.pathname + location.search });
  });
}

const qualityAssurancePage = Loadable({
  loader: () => import('./pages/quality-assurance/quality-assurance'),
  loading: BouncingLoader
})

const siteManagementPage = Loadable({
  loader: () => import('./pages/site-management/site-management'),
  loading: BouncingLoader
})

const referenceDataPage = Loadable({
  loader: () => import('./pages/reference-data/reference-data'),
  loading: BouncingLoader
})

const campaignManagementPage = Loadable({
  loader: () => import('./pages/campaign-management/campaign-management'),
  loading: BouncingLoader
})

const campaignPreviewPage = Loadable({
  loader: () => import('./pages/campaign-preview/campaign-preview'),
  loading: BouncingLoader
})

const campaignSummaryPage = Loadable({
  loader: () => import('./pages/campaign-summary/campaign-summary'),
  loading: BouncingLoader
})

const inspectionSummaryPage = Loadable({
  loader: () => import('./pages/inspection-summary/inspection-summary'),
  loading: BouncingLoader
})

const inspectionHistoryPage = Loadable({
  loader: () => import('./pages/inspection-history/inspection-history'),
  loading: BouncingLoader
})

const inspectorSummaryPage = Loadable({
  loader: () => import('./pages/inspector-summary/inspector-summary'),
  loading: BouncingLoader
})

const issueResolutionPage = Loadable({
  loader: () => import('./pages/issue-resolution/issue-resolution'),
  loading: BouncingLoader
})

const agencyDashboardPage = Loadable({
  loader: () => import('./pages/agency-dashboard/agency-dashboard'),
  loading: BouncingLoader
})

const agencyDashboardCampaignDetailPage = Loadable({
  loader: () => import('./pages/agency-dashboard/campaign-detail/campaign-detail'),
  loading: BouncingLoader
})

const agencyDashboardSiteListPage = Loadable({
  loader: () => import('./pages/agency-dashboard/site-list/site-list'),
  loading: BouncingLoader
})

const settingsPage = Loadable({
  loader: () => import('./pages/settings/settings'),
  loading: BouncingLoader
})

const inspectorsPage = Loadable({
  loader: () => import('./pages/inspectors/inspectors'),
  loading: BouncingLoader
})

const usersPage = Loadable({
  loader: () => import('./pages/users/users'),
  loading: BouncingLoader
})

const formatsPage = Loadable({
  loader: () => import('./pages/formats/formats'),
  loading: BouncingLoader
})

const advertisersPage = Loadable({
  loader: () => import('./pages/advertisers/advertisers'),
  loading: BouncingLoader
})

const dspsPage = Loadable({
  loader: () => import('./pages/dsps/dsps'),
  loading: BouncingLoader
})

const reprocessQueuePage = Loadable({
  loader: () => import('./pages/reprocess-queue/reprocess-queue'),
  loading: BouncingLoader
});

const publisherPerformancePage = Loadable({
  loader: () => import('./pages/publisher-performance/publisher-performance'),
  loading: BouncingLoader
});

const appUsersPage = Loadable({
  loader: () => import('./pages/app-users/app-users'),
  loading: BouncingLoader
})

const mediaOwnersPage = Loadable({
  loader: () => import('./pages/media-owners'),
  loading: BouncingLoader
})

const agenciesPage = Loadable({
  loader: () => import('./pages/agencies'),
  loading: BouncingLoader
})

const supportedBrowser = () => {
  // simple checks to make sure the browser is supported

  if (!navigator.clipboard) {
    return false;
  }

  return true;
}

const getDefaultPathName = (role) => {
  var pathName = LOGOUT_URL_PATH;

  switch (role) {
    case ROLE_OIS_ADMIN:
    case ROLE_OIS_STAFF:
    case ROLE_MEDIA_AGENCY:
    case ROLE_CREATIVE_AGENCY:
    case ROLE_MEDIA_OWNER:
      pathName = '/campaign-management/7-days';
      break;
    default:
      break;
  }

  return pathName;
};

const DefaultRoute = ({ component: C, ...rest }) => (
  <AuthContext.Consumer>
    {({ token, role }) => (
      <Route
        {...rest}
        render={(props) => (token
          ? (<Redirect to={{ pathname: getDefaultPathName(role), state: { from: props.location } }} />)
          : (<Redirect to={{ pathname: LOGOUT_URL_PATH, state: { from: props.location } }} />)
        )}
      />
    )}
  </AuthContext.Consumer>
)

const isRoleAuthorized = (role, superUser, access, url, params = {}) => {
  var permissions = [];

  switch (role) {
    case ROLE_OIS_ADMIN:
      permissions = [
        '/dashboard',
        `/dashboard/${params.dateRange}`,
        `/dashboard/${params.dateRange}/${params.campaignId}`,
        `/dashboard/${params.dateRange}/${params.campaignId}/${params.siteListId}`,
        '/site-management',
        '/reference-data',
        '/reference-data/imports',
        '/campaign-management',
        `/campaign-management/${params.dateRange}`,
        `/campaign-preview/${params.id}`,
        '/inspection-summary',
        '/inspector-summary',
        '/inspection-history',
        `/inspection-history/${params.startDate}/${params.endDate}`,
        '/quality-assurance',
        '/campaign-summary',
        '/issue-resolution',
        `/issue-resolution/${params.id}`,
        '/campaigns/management',
        '/campaigns/new',
        `/campaigns/${params.id}`,
        `/campaigns/${params.id}/setup`,
        '/settings',
        '/inspectors',
        `/inspectors/${params.inspectorId}`,
        '/app-users',
        '/users',
        `/users/${params.userId}`,
        '/reprocess-queue',
        '/formats',
        `/formats/${params.id}`,
        '/advertisers',
        `/advertisers/${params.id}`,
        '/dsps',
        `/dsps/${params.id}`,
        '/reports/publisher-performance',
        '/media-owners',
        `/media-owners/${params.id}`,
        '/agencies',
        `/agencies/${params.id}`
      ];
      break;
    case ROLE_OIS_STAFF:
      permissions = [
        '/dashboard',
        `/dashboard/${params.dateRange}`,
        `/dashboard/${params.dateRange}/${params.campaignId}`,
        `/dashboard/${params.dateRange}/${params.campaignId}/${params.siteListId}`,
        '/site-management',
        '/reference-data',
        '/reference-data/imports',
        '/campaign-management',
        `/campaign-management/${params.dateRange}`,
        `/campaign-preview/${params.id}`,
        '/quality-assurance',
        '/inspection-summary',
        '/inspector-summary',
        '/inspection-history',
        `/inspection-history/${params.startDate}/${params.endDate}`,
        '/campaign-summary',
        '/issue-resolution',
        `/issue-resolution/${params.id}`,
        '/campaigns/management',
        '/campaigns/new',
        `/campaigns/${params.id}`,
        `/campaigns/${params.id}/setup`,
        '/users',
        '/settings'
        // '/reports/publisher-performance',
      ];
      break;
    case ROLE_MEDIA_AGENCY:
      permissions = [
        '/dashboard',
        `/dashboard/${params.dateRange}`,
        `/dashboard/${params.dateRange}/${params.campaignId}`,
        `/dashboard/${params.dateRange}/${params.campaignId}/${params.siteListId}`,
        '/campaign-management',
        `/campaign-management/${params.dateRange}`,
        `/campaign-preview/${params.id}`,
        '/settings',
        '/users'
      ];

      if (superUser) {
        permissions.push(`/users/${params.userId}`);

        if (access.indexOf('app-user-management') > -1) {
          permissions.push('/app-users');
          permissions.push(`/app-users/${params.id}`);
        }
      }

      if (access.indexOf('advertiser-management') > -1) {
        permissions.push('/advertisers');
        permissions.push(`/advertisers/${params.id}`);
      }

      if (access.indexOf('report-publisher-performance') > -1) {
        permissions.push('/reports/publisher-performance');
      }

      break;
    case ROLE_CREATIVE_AGENCY:
      permissions = [
        '/campaign-management',
        `/campaign-management/${params.dateRange}`,
        '/users',
        '/settings'
      ];
      break;
    case ROLE_MEDIA_OWNER:
      permissions = [
        '/dashboard',
        `/dashboard/${params.dateRange}`,
        `/dashboard/${params.dateRange}/${params.campaignId}`,
        `/dashboard/${params.dateRange}/${params.campaignId}/${params.siteListId}`,
        '/issue-resolution',
        `/issue-resolution/${params.id}`,
        '/campaign-management',
        `/campaign-management/${params.dateRange}`,
        '/settings',
        '/users'
      ];

      if (superUser) {
        permissions.push(`/users/${params.userId}`);

        if (access.indexOf('app-user-management') > -1) {
          permissions.push('/app-users');
          permissions.push(`/app-users/${params.id}`);
        }
      }

      if (access.indexOf('reference-data-management') > -1) {
        permissions.push('/reference-data');
      }

      if (access.indexOf('reference-data-import') > -1) {
        permissions.push('/reference-data/imports');
      }

      break;
    default:
      break;
  }

  return permissions.includes(url)
}

const ProtectedRoute = ({ component: C, ...rest }) => {
  const { role, token, name, email, userId, countryCode, signOut } = useContext(AuthContext);
  const [ globalState ] = useContext(GlobalStoreContext);

  if (globalState.loading) {
    return <BouncingLoader />;
  }

  return (
    <Route
      {...rest} render={(props) => {
        // google analytics tracking
        googleAnalyticsTracking(props.history);

        if (!supportedBrowser()) {
          return (<Redirect to='/browser-support' />);
        }

        if (token === undefined || token === null || token.length === 0) {
          signOut();
          return;
          // return (<Redirect to={{ pathname: LOGOUT_URL_PATH, state: { from: props.location } }} />);
        }

        // remove trailing slash from the url
        let currentUrl = props.match.url;
        if (currentUrl.endsWith('/')) {
          currentUrl = currentUrl.slice(0, currentUrl.length - 1);
        }

        if (currentUrl === '/reports/format-performance') {
          return (<Redirect to={`/reports/publisher-performance${props.location.search}`} />);
        }

        // handle dashboard-v3 redirects
        if (globalState.dashboard3.some(url => currentUrl.startsWith(url))) {
          // redirect to new dashboard
          const url = process.env.DASHBOARD_V3_URL + currentUrl + window.location.search;
          window.location.replace(url);

          return;
        }

        if (!isRoleAuthorized(role, globalState.superUser, globalState.access, currentUrl, props.match.params)) {
          return (<Redirect to={{ pathname: '/unauthorize', state: { from: props.location } }} />);
        }

        // check for default params
        if (rest.defaultParams) {
          let redirectUrl = currentUrl;
          let redirect = false;

          // see if we're missing any default params and build up the redirect url
          for (const key in rest.defaultParams) {
            // eslint-disable-next-line no-prototype-builtins
            if (rest.defaultParams.hasOwnProperty(key)) {
              redirectUrl += `/${rest.defaultParams[key]}`;
              if (props.match.params[key] === undefined) {
                redirect = true;
              }
            }
          }

          // redirect to the default url if we're missing any params
          if (redirect) {
            return (
              <Redirect to={`${redirectUrl}${props.location.search}`} />
            );
          }
        }

        // render route
        return rest.hideTemplate ? (
          <C
            name={name}
            email={email}
            role={role}
            userId={userId}
            countryCode={countryCode}
            token={token}
            {...props}
          />
        ) : (
          <div className='ois-simple-theme'>
            <Fragment>
              <Sidebar {...props} role={role} countryCode={countryCode} superUser={globalState.superUser} access={globalState.access} dashboard3Routes={globalState.dashboard3} simpleTheme={true} name={name} signOut={signOut} />
              <div className='main'>
                <h1 className='page-title'>{rest.pageTitle}</h1>
                <C
                  name={name}
                  email={email}
                  role={role}
                  userId={userId}
                  token={token}
                  {...props}
                />
              </div>
            </Fragment>
          </div>
        )
      }}
    />
  );
}

const UnprotectedRoute = ({ ...rest }) => {
  const { role, name, countryCode, signOut } = useContext(AuthContext);
  const [ globalState ] = useContext(GlobalStoreContext);

  if (globalState.loading) {
    return <BouncingLoader />;
  }

  return (
    <Route
      {...rest} render={(props) => {
        // google analytics tracking
        googleAnalyticsTracking(props.history);

        // render route
        return (
          <div className='ois-simple-theme'>
            <Fragment>
              <Sidebar {...props} role={role} countryCode={countryCode} superUser={globalState.superUser} access={globalState.access} dashboard3Routes={globalState.dashboard3} simpleTheme={true} name={name} signOut={signOut} />
              <div className='main'>
                <h1 className='page-title'>{rest.pageTitle}</h1>
                <div className='container'>
                  <div className='section__body'>
                    {rest.content}
                  </div>
                </div>
              </div>
            </Fragment>
          </div>
        );
      }}
    />
  );
}

const Routes = () => (
  <Router>
    <AuthProvider>
      <GlobalStore>
        <Switch>
          <DefaultRoute exact path='/' />
          <ProtectedRoute exact path='/dashboard/:dateRange?' component={agencyDashboardPage} defaultParams={{ dateRange: '7-days' }} pageTitle='Dashboard' />
          <ProtectedRoute exact path='/dashboard/:dateRange/:campaignId' component={agencyDashboardCampaignDetailPage} pageTitle='Campaign Overview' />
          <ProtectedRoute exact path='/dashboard/:dateRange/:campaignId/:siteListId' component={agencyDashboardSiteListPage} pageTitle='Format Overview' />
          <ProtectedRoute exact path='/issue-resolution' component={issueResolutionPage} pageTitle='Issue Resolution' />
          <ProtectedRoute exact path='/issue-resolution/:id' component={issueResolutionPage} pageTitle='Issue Resolution' />
          <ProtectedRoute exact path='/site-management' component={siteManagementPage} pageTitle='Site Management' />
          <ProtectedRoute exact path='/reference-data' component={referenceDataPage} pageTitle='Reference Data' />
          <ProtectedRoute exact path='/reference-data/imports' component={referenceDataPage} pageTitle='Reference Data' />
          <ProtectedRoute exact path='/campaign-management/:dateRange?' component={campaignManagementPage} defaultParams={{ dateRange: '7-days' }} pageTitle={(<img src={`${process.env.STATIC_URL}images/logo-campaign-manager.png`} className='page-title-img' />)} />
          <ProtectedRoute exact path='/inspection-summary' component={inspectionSummaryPage} pageTitle='Inspection Summary' />
          <ProtectedRoute exact path='/inspector-summary' component={inspectorSummaryPage} pageTitle='Inspector Summary' />
          <ProtectedRoute exact path='/inspection-history/:startDate?/:endDate?' component={inspectionHistoryPage} defaultParams={{ startDate: defaultDates.startDate, endDate: defaultDates.endDate }} pageTitle='Inspection History' />
          <ProtectedRoute exact path='/quality-assurance' component={qualityAssurancePage} pageTitle='Quality Assurance' />
          <ProtectedRoute exact path='/campaign-summary' component={campaignSummaryPage} pageTitle='Campaign Summary' />
          <ProtectedRoute exact path='/settings' component={settingsPage} pageTitle='Settings' />
          <ProtectedRoute exact path='/inspectors' component={inspectorsPage} pageTitle='Inspectors' />
          <ProtectedRoute exact path='/inspectors/:inspectorId' component={inspectorsPage} pageTitle='Inspectors' />
          <ProtectedRoute exact path='/users' component={usersPage} pageTitle='Users' />
          <ProtectedRoute exact path='/users/:userId' component={usersPage} pageTitle='Users' />
          <ProtectedRoute exact path='/campaign-preview/:id' component={campaignPreviewPage} hideTemplate={true} />
          <ProtectedRoute exact path='/formats' component={formatsPage} pageTitle='Formats' />
          <ProtectedRoute exact path='/formats/:id' component={formatsPage} pageTitle='Formats' />
          <ProtectedRoute exact path='/advertisers' component={advertisersPage} pageTitle='Advertisers' />
          <ProtectedRoute exact path='/advertisers/:id' component={advertisersPage} pageTitle='Advertisers' />
          <ProtectedRoute exact path='/dsps' component={dspsPage} pageTitle='DSPs' />
          <ProtectedRoute exact path='/dsps/:id' component={dspsPage} pageTitle='DSPs' />
          <ProtectedRoute exact path='/reprocess-queue' component={reprocessQueuePage} pageTitle='Reprocess Queue' />
          <ProtectedRoute exact path='/app-users' component={appUsersPage} pageTitle='App users' />
          <ProtectedRoute exact path='/app-users/:id' component={appUsersPage} pageTitle='App users' />
          <ProtectedRoute exact path='/media-owners' component={mediaOwnersPage} pageTitle='Media Owners' />
          <ProtectedRoute exact path='/media-owners/:id' component={mediaOwnersPage} pageTitle='Media Owners' />
          <ProtectedRoute exact path='/agencies' component={agenciesPage} pageTitle='Agencies' />
          <ProtectedRoute exact path='/agencies/:id' component={agenciesPage} pageTitle='Agencies' />
          <ProtectedRoute exact path={['/reports/format-performance', '/reports/publisher-performance']} component={publisherPerformancePage} pageTitle='Publisher Performance' />

          <UnprotectedRoute exact path='/unauthorize' pageTitle='Unauthorized' content='You are not authorized to view this page' />
          <Route exact path='/browser-support' render={(props) => (<div>This browser is not supported, please upgrade.</div>)} />
          <Route path='/accounts/logout' render={(props) => <Redirect to={LOGOUT_URL_PATH} />} />
          <UnprotectedRoute pageTitle='404 - Not Found' content='Page does not exist' />
        </Switch>
      </GlobalStore>
    </AuthProvider>
  </Router>
)

export default Routes
